<template>
  <div
    class="file-uploader"
    style="color: #fff; background-color: rgb(43, 45, 51)"
  >
    <!-- 上传区域 -->
    <div
      class="upload-area"
      @click="$refs.fileInput.click()"
      @dragover.prevent
      @drop.prevent="handleDrop"
    >
      <input
        ref="fileInput"
        type="file"
        :accept="accept"
        :multiple="multiple"
        class="hidden-input"
        @change="handleFileChange"
      />

      <v-icon :icon="icon" color="primary" size="48" />

      <div class="text-h6 mt-4">
        {{ files?.length ? "点击或拖拽更多文件" : "点击或拖拽上传文件" }}
      </div>

      <div
        class="text-body-2 text-medium-emphasis mt-2"
        style="color: rgba(255, 255, 255, 0.26) !important"
      >
        {{ acceptText }}
        <br />
        {{ multiple ? "可同时上传多个文件" : "请选择一个文件" }}
        <br />
        建议使用 Chrome、Edge、Safari 浏览器
      </div>
    </div>

    <!-- 文件列表 -->
    <v-list v-if="files?.length" class="file-list mt-4" color="#2B2D33">
      <v-list-item
        v-for="(file, index) in files"
        :key="index"
        class="file-item"
        color="#3A3B3F"
      >
        <template v-slot:prepend>
          <v-icon
            :color="isValidFileType(file) ? 'primary' : 'error'"
            :icon="isValidFileType(file) ? 'mdi-file' : 'mdi-file-alert'"
          />
        </template>

        <v-list-item-title>
          {{ file.name }}
        </v-list-item-title>

        <v-list-item-subtitle>
          {{ formatFileSize(file.size) }}
          <span v-if="!isValidFileType(file)" class="text-error">
            - 不支持的文件格式
          </span>
        </v-list-item-subtitle>

        <template v-slot:append>
          <v-btn
            icon="mdi-close"
            variant="text"
            size="small"
            @click="removeFile(index)"
          />
        </template>
      </v-list-item>
    </v-list>

    <!-- 上传进度 -->
    <div v-if="uploading" class="progress-container mt-4">
      <div class="d-flex align-start">
        <v-icon icon="mdi-cloud-upload" color="primary" class="mr-2" />
        <div class="progress-info">
          正在上传 {{ uploadedCount }}/{{ totalCount }} ({{
            Math.round(uploadProgress)
          }}%)
        </div>
      </div>
    </div>

    <!-- 错误提示 -->
    <v-alert
      v-if="error"
      type="error"
      variant="tonal"
      class="mt-4"
      closable
      @click:close="error = null"
    >
      {{ error }}
    </v-alert>

    <!-- 底部操作栏 -->
    <div class="d-flex justify-end mt-4">
      <v-btn
        color="error"
        variant="tonal"
        :disabled="uploading"
        @click="handleClose"
        style="margin-right: 16px"
      >
        取消
      </v-btn>
      <v-btn
        color="primary"
        variant="flat"
        :loading="uploading"
        :disabled="!canUpload"
        @click="handleUpload"
      >
        <template v-if="uploading">
          {{ uploadedCount }}/{{ totalCount }} ({{
            Math.round(uploadProgress)
          }}%)
        </template>
        <template v-else> 开始上传 </template>
      </v-btn>
    </div>
  </div>
</template>
  
  <script setup lang="ts">
import { ref, computed } from "vue";
import { uploadApi } from "@/services/api";

const dialogVisible = computed({
  get() {
    return props.modelValue;
  },
  set(value) {
    emit("update:modelValue", value);
  },
});

const props = defineProps<{
  accept?: string;
  multiple?: boolean;
  icon?: string;
  acceptText?: string;
}>();

const emit = defineEmits<{
  (e: "upload", files: File[]): void;
}>();

const fileInput = ref<HTMLInputElement | null>(null);
const files = ref<File[] | null>(null);
const uploading = ref(false);
const uploadProgress = ref(0);
const error = ref<string | null>(null);
const uploadedCount = ref(0);
const totalCount = ref(0);

// 是否可以上传
const canUpload = computed(() => {
  if (!files.value?.length) return false;
  return files.value.every((file) => isValidFileType(file));
});

// 验证文件类型
const isValidFileType = (file: File) => {
  if (!props.accept) return true;
  const acceptedTypes = props.accept.split(",").map((type) => type.trim());
  return acceptedTypes.some((type) => {
    if (type.startsWith(".")) {
      return file.name.toLowerCase().endsWith(type.toLowerCase());
    }
    return file.type.match(type);
  });
};

// 格式化文件大小
const formatFileSize = (bytes: number) => {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

// 处理文件选择
const handleFileChange = (event: Event) => {
  const input = event.target as HTMLInputElement;
  if (input.files) {
    files.value = Array.from(input.files);
  }
};

// 处理拖放
const handleDrop = (event: DragEvent) => {
  const droppedFiles = event.dataTransfer?.files;
  if (droppedFiles) {
    files.value = Array.from(droppedFiles);
  }
};

// 移除文件
const removeFile = (index: number) => {
  if (files.value) {
    files.value.splice(index, 1);
    if (files.value.length === 0) {
      files.value = null;
    }
  }
};

// 处理关闭
const handleClose = () => {
  if (uploading.value) return;
  dialogVisible.value = false;
  files.value = null;
  emit("upload", []);
};

// 处理上传
const handleUpload = async () => {
  if (!files.value || !canUpload.value) return;

  uploading.value = true;
  uploadProgress.value = 0;
  uploadedCount.value = 0;
  totalCount.value = files.value.length;

  try {
    console.log("开始上传文件:", files.value);
    // 使用新的批量上传接口
    const uploadedUrls = await uploadApi.uploadFiles(
      files.value,
      (progress) => {
        uploadProgress.value = progress;
        console.log("总体上传进度:", progress);
      },
      (fileIndex, progress) => {
        // 当一个文件完成上传时更新计数
        if (progress === 100) {
          uploadedCount.value = fileIndex + 1;
        }
        console.log(
          `文件 ${files.value[fileIndex].name} 上传进度: ${progress}%`
        );
      }
    );
    var newUploadedUrls = [];
    for (let i = 0; i < uploadedUrls.length; i++) {
      newUploadedUrls.push({
        url: uploadedUrls[i],
        name: files.value[i].name,
      });
    }
    console.log("文件上传完成，URLs:", uploadedUrls);
    // 发送上传完成的事件
    emit("upload", newUploadedUrls);

    // 清理状态
    files.value = null;
    dialogVisible.value = false;
  } catch (err: any) {
    console.error("上传过程发生错误:", err);
    error.value = err.message || "上传失败，请重试";
  } finally {
    uploading.value = false;
    uploadProgress.value = 0;
    uploadedCount.value = 0;
    totalCount.value = 0;
  }
};
</script>
  
  <style scoped>
.file-uploader {
  background-color: #2b2d33;
  border-radius: 8px;
  padding: 20px;
}

.upload-area {
  border: 2px dashed rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  padding: 40px;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.3s;
}

.upload-area:hover {
  border-color: var(--v-theme-primary);
}

.hidden-input {
  display: none;
}

.file-list {
  border-radius: 8px;
  overflow: hidden;
}

.file-item {
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
}

.file-item:last-child {
  border-bottom: none;
}

.progress-container {
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 12px;
}

.progress-info {
  color: rgba(255, 255, 255, 0.7);
}

.file-list {
  border-radius: 8px;
  overflow: hidden;
  background-color: #3a3b3f !important;
}

.v-list-item-title{
  color:rgba(255, 255, 255, 0.7) !important
}

.v-list-item-subtitle{
  color:rgba(255, 255, 255, 0.26) !important
}

:deep(.mdi-file-document){
  color: #fff !important;
}
</style> 